import './index.scss';
import { useEffect, MouseEvent } from 'react';
import { Paper, Table, TableBody, TableContainer } from '@material-ui/core';
import { setAnswerFiltersPartially } from '@actions/answersPageAction';
import { useDispatch, useSelector } from 'react-redux';
import {
  answersRowsSelector,
  answersPageNumberSelector,
  answersRowsPerPageSelector,
} from '@selectors/answer';
import { Pagination } from '@components/Pages/AnswersPage/constants';
import { AnswerRow, MgState } from '@types';
import { AnswerControllersContainer } from '@components/Pages/AnswersPage/AnswerControllers';
import { TableHeader } from '@components/Pages/AnswersPage/TableHeader';
import { TableRowAnswer } from '@components/Pages/AnswersPage/TableRow/TableRow';
import { setCheckedAnswers } from '@actions/answerActions';
import { AnswerTablePagination } from '@components/Pages/AnswersPage/AnswerTablePagination';

export const AnswersPage = () => {
  const dispatch = useDispatch();

  const answers = useSelector(answersRowsSelector);
  const checkedAnswersIds = useSelector<MgState, string[]>(
    ({ answer }) => answer.checkedAnswersIds
  );
  const pageNumber = useSelector(answersPageNumberSelector);
  const rowsPerPage = useSelector(answersRowsPerPageSelector);
  const isAnswersLoading = useSelector<MgState, boolean>(({ answer }) => answer.isAnswersLoading);
  /** Define filters w/o sent status to have not initial state as []
   */
  useEffect(() => {
    dispatch(setAnswerFiltersPartially({ offset: pageNumber * rowsPerPage }, true));
  }, []);

  const handleChangePage = (_, newPage: number): void => {
    if (isAnswersLoading) {
      return;
    }
    dispatch(setAnswerFiltersPartially({ offset: (newPage - 1) * rowsPerPage }));
  };

  const handleChangePageNumber = (value: number) => {
    if (isAnswersLoading) {
      return;
    }

    if (value) {
      dispatch(setAnswerFiltersPartially({ offset: (value - 1) * rowsPerPage }));
    }
  };

  const handleChangeRowsPerPage = (value) => {
    if (isAnswersLoading) {
      return;
    }
    const answersOnPageCount = parseInt(value || Pagination.ANSWERS_ON_PAGE_COUNT_FIRST);
    dispatch(setAnswerFiltersPartially({ offset: 0, limit: answersOnPageCount }));
  };

  const handleCheckboxChange = (answer: AnswerRow) => {
    const isChecked = !!checkedAnswersIds.filter((id) => id === answer.id).length;
    const result = isChecked
      ? checkedAnswersIds.filter((id) => id !== answer.id)
      : [...checkedAnswersIds, answer.id];

    dispatch(setCheckedAnswers(result));
  };

  const handleCheckboxClick = (event: MouseEvent<HTMLElement>, answer: AnswerRow) => {
    event.preventDefault();
    handleCheckboxChange(answer);
  };

  return (
    <div className="answers-page">
      <AnswerControllersContainer />
      <Paper className="grid">
        <TableContainer>
          <Table size="small" data-testid="table">
            <TableHeader />
            <TableBody>
              {answers.map((answer, index) => {
                return (
                  <TableRowAnswer
                    key={answer.id}
                    answer={answer}
                    order={index + 1}
                    handleCheckboxClick={handleCheckboxClick}
                    page={pageNumber}
                    rowsPerPage={rowsPerPage}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <AnswerTablePagination
        handleChangePage={handleChangePage}
        handleChangePageNumber={handleChangePageNumber}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </div>
  );
};
